import React, { useCallback, useState, useEffect } from "react";
import toastUtils from "../../utils/toastUtils";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FaCopy, FaCheckCircle } from 'react-icons/fa';
import {
  Grid,
  FormControl,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  Switch,
  TextField,
  MenuItem,
  Typography,
  Box,
  Button,
  CircularProgress,
  Slider,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Tune as TuneIcon,
  AssessmentOutlined,
  Memory as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  VolumeUp as VolumeUpIcon,
  Mic as MicIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Language as LanguageIcon,
  Group as GroupIcon,
  Subject as SummarizeIcon
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n.js";
import api from "../../services/api";
import useSettings from "../../hooks/useSettings/index.js";

// Constantes
const openAiModels = [
  { value: "o1-preview", label: "O1 Preview" },
  { value: "o1-mini", label: "O1 Mini" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "gpt-4", label: "GPT-4" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" }
];

const voiceOptions = [
  { value: 'alloy', label: 'Alloy' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'nova', label: 'Nova' },
  { value: 'shimmer', label: 'Shimmer' }
];

// Componente de Slider
const SettingSlider = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  icon,
  marks = [],
  formatLabel = (val) => val,
  disabled = false
}) => {
  const handleChange = useCallback((_, newValue) => {
    onChange(id, newValue);
  }, [id, onChange]);

  return (
    <Box
      style={{
        padding: '20px',
        border: '1px solid #e0e0e0',
        borderColor: '#e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s'
      }}
    >
      <Box style={{ display: "flex", alignItems: "center", marginBottom: '8px' }}>
        {icon}
        <Typography variant="subtitle2" fontWeight={600}>
          {title}: {formatLabel(value)}
        </Typography>
      </Box>

      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}

      <Slider
        value={value || min}
        onChange={handleChange}
        min={min}
        max={max}
        step={step}
        marks={marks}
        disabled={disabled}
        valueLabelDisplay="auto"
        valueLabelFormat={formatLabel}
      />
    </Box>
  );
});

// Componente de Switch Otimizado
const SettingSwitch = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  disabled = false,
  icon,
  color = "primary"
}) => {
  const handleChange = useCallback((event) => {
    onChange(id, event.target.checked ? "enabled" : "disabled");
  }, [id, onChange]);

  return (
    <Box
      style={{
        padding: '20px',
        border: '1px solid #e0e0e0',
        borderColor: '#e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s'
      }}
    >
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={value === "enabled"}
              onChange={handleChange}
              color={color}
              disabled={disabled}
            />
          }
          label={
            <Box>
              <Box style={{ display: "flex", alignItems: "center", marginBottom: '4px' }}>
                {icon}
                <Typography variant="subtitle2" fontWeight={600}>
                  {title}
                </Typography>
              </Box>
              {description && (
                <Typography variant="body2" color="textSecondary">
                  {description}
                </Typography>
              )}
            </Box>
          }
        />
      </FormGroup>
    </Box>
  );
});

const SettingTextField = React.memo(({
  id,
  label,
  description,
  title,
  value,
  onChange,
  helpText,
  icon,
  type = "text",
  gridSize = { xs: 12, sm: 6 },
  validation = null,
  showCopyButton = false,
  notifyBackend = true
}) => {
  const [copied, setCopied] = useState(false);

  const handleChange = useCallback((event) => {
    const newValue = event.target.value;
    if (validation && !validation(newValue)) {
      return;
    }
    onChange(id, newValue, notifyBackend);
  }, [id, onChange, validation, notifyBackend]);

  const handleCopy = () => {
    setTimeout(() => {
      setCopied(false);
    }, 1 * 1000);
    setCopied(true);
  };

  return (
    <Box
      style={{
        padding: '20px',
        border: '1px solid #e0e0e0',
        borderColor: '#e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s'
      }}
    >
      <Grid container spacing={2}>
        <Grid item {...gridSize}>
          <Box style={{ display: "flex", alignItems: "center", marginBottom: '12px' }}>
            {icon}
            <Typography variant="subtitle2" fontWeight={600}>
              {title}
            </Typography>
          </Box>

          {description && (
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
              {description}
            </Typography>
          )}

          <TextField
            id={id}
            name={id}
            label={label}
            type={type}
            value={value || ""}
            onChange={handleChange}
            variant="outlined"
            size="small"
            fullWidth
            margin="normal"
            autoComplete={type === "password" ? "current-password" : "off"}
            form="settings-form"
            InputProps={{
              startAdornment: icon && (
                <Box style={{ display: "flex", alignItems: "center", marginRight: '8px' }}>
                  {icon}
                </Box>
              ),
              endAdornment: showCopyButton && value && (
                <Tooltip title="Copiar valor">
                  <CopyToClipboard text={value} onCopy={handleCopy}>
                    <button className="copy-button" type="button">
                      {copied ? (
                        <>
                          <span>{i18n.t("settings.ia.copied")}</span>
                          <FaCheckCircle size={18} />
                        </>
                      ) : (
                        <>
                          <span>{i18n.t("settings.ia.copyCode")}</span>
                          <FaCopy size={18} />
                        </>
                      )}
                    </button>
                  </CopyToClipboard>
                </Tooltip>
              ),
            }}
          />
        </Grid>
      </Grid>
      {helpText && (
        <FormHelperText style={{ marginTop: '8px', marginLeft: '16px' }}>
          {helpText}
        </FormHelperText>
      )}
    </Box>
  );
});

// Componente de Select Otimizado
const SettingSelect = React.memo(({
  id,
  title,
  description,
  value,
  options,
  onChange,
  icon,
  disabled = false,
  fullWidth = true
}) => {
  const handleChange = useCallback((event) => {
    onChange(id, event.target.value);
  }, [id, onChange]);

  return (
    <Box
      style={{
        padding: '20px',
        border: '1px solid #e0e0e0',
        borderColor: '#e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s'
      }}
    >
      <Box style={{ display: "flex", alignItems: "center", marginBottom: '12px' }}>
        {icon}
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
      </Box>

      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}

      <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
        <TextField
          select
          value={value}
          onChange={handleChange}
          variant="outlined"
          disabled={disabled}
          size="small"
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    </Box>
  );
});

/**
 * OpenAI Configuration Component
 * 
 * This component manages the centralized OpenAI configuration for the entire company.
 * All assistants and prompts use this centralized configuration instead of individual API keys.
 * 
 * Features:
 * - API key validation with connection testing
 * - Model selection (GPT-4o, GPT-3.5-turbo, etc.)
 * - Temperature and max tokens configuration
 * - Audio transcription settings
 * - Voice synthesis settings
 * - Group summary settings
 * 
 * Requirements: 1.1, 1.2 from fix-openai-integration spec
 */
const IA = ({ oldSettings }) => {
  const { update } = useSettings();
  
  const [validatingApiKey, setValidatingApiKey] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState({});
  const [apiKeyValidation, setApiKeyValidation] = useState({
    isValid: null,
    error: null,
    lastCheck: null,
    errorCode: null
  });

  useEffect(() => {
    let newSettings = {};
    for (const { key, value } of oldSettings) {
      newSettings[key] = value;
    }

    setSettingsLoaded(newSettings);
  }, [oldSettings]);

  // Handler para mudanças - DEFINIDO ANTES de ser usado
  const handleSettingChange = useCallback(async (key, value, notifyBackend = true) => {
    console.log('handleSettingChange called:', { key, value, notifyBackend });

    setSettingsLoaded(prevSettings => {
      return {
        ...prevSettings,
        [key]: value
      };
    });

    if (notifyBackend) {
      try {
        console.log('Saving setting to backend:', { key, value });
        const result = await update({ key, value });
        console.log('Setting saved successfully:', result);

        // Mostrar feedback de sucesso apenas para campos importantes
        if (['openAiKey', 'openaiModel', 'aiProvider'].includes(key)) {
          toastUtils.success("Configuração Salva", `${key} foi atualizado com sucesso`);
        }
      } catch (error) {
        console.error('Error saving setting:', error);
        // Reverter a mudança local se falhou no backend
        setSettingsLoaded(prevSettings => {
          const newSettings = { ...prevSettings };
          delete newSettings[key];
          return newSettings;
        });

        // Mostrar erro específico ao usuário
        const errorMessage = (error.response && error.response.data && error.response.data.message) ||
                           `Erro ao salvar configuração ${key}`;
        toastUtils.error("Erro ao Salvar", errorMessage);

        throw error;
      }
    }

    if (key === 'openAiKey') {
      setApiKeyValidation({ isValid: null, error: null, lastCheck: null });
    }
  }, [update]);

    // Validação para números
  const numberValidation = useCallback((value) => {
    return value === "" || /^[0-9\b]+$/.test(value);
  }, []);

    // Validação da chave da API OpenAI
  const validateOpenAIKey = useCallback(async (apiKey) => {
    // Validação básica no frontend
    if (!apiKey || typeof apiKey !== 'string') {
      setApiKeyValidation({
        isValid: false,
        error: i18n.t("settings.settings.ia.apiKeyRequired"),
        lastCheck: new Date(),
        errorCode: 'MISSING_KEY'
      });
      return;
    }

    // Validação de formato básico
    if (!apiKey.startsWith('sk-')) {
      setApiKeyValidation({
        isValid: false,
        error: i18n.t("settings.settings.ia.invalidApiKeyFormat"),
        lastCheck: new Date(),
        errorCode: 'INVALID_FORMAT'
      });
      return;
    }

    setValidatingApiKey(true);
    setApiKeyValidation({
      isValid: null,
      error: null,
      lastCheck: null,
      errorCode: null
    });

    try {
      // Try the new standardized endpoint first, fallback to legacy if needed
      let response;
      try {
        response = await api.post('/settings/openai-config/test', {
          openAiKey: apiKey.trim(),
        });
      } catch (newEndpointError) {
        // Fallback to legacy endpoint for backward compatibility
        response = await api.post('/settings/openai-validate-config', {
          openaiApiKey: apiKey.trim(),
        });
      }

      // Verificar se a resposta é válida
      if (response.data && response.data.status === 'success' && response.data.isValid) {
        setApiKeyValidation({
          isValid: true,
          error: null,
          lastCheck: new Date(),
          errorCode: null,
          validatedAt: response.data.data && (response.data.data.validatedAt || response.data.data.testedAt),
          modelsAvailable: response.data.data && (response.data.data.modelsAvailable || response.data.data.model)
        });

        const modelsInfo = response.data.data && response.data.data.modelsAvailable 
          ? response.data.data.modelsAvailable 
          : (response.data.data && response.data.data.model ? 1 : 0);

        toastUtils.success("Configurações Salvas", i18n.t("settings.settings.ia.apiKeyValidatedSuccess", {
          models: modelsInfo
        }));

        // Chave validada com sucesso - log removido por segurança
      } else {
        throw new Error('Resposta inválida do servidor');
      }

    } catch (error) {
      // Error log removido por segurança - não expor detalhes de erro com chaves API

      let errorMessage = i18n.t("settings.settings.ia.apiKeyValidationError");
      let errorCode = 'UNKNOWN_ERROR';

      if (error.response && error.response.data) {
        const { data } = error.response;
        errorMessage = data.message || errorMessage;
        errorCode = data.errorCode || errorCode;

        // Mensagens específicas baseadas no código de erro
        switch (errorCode) {
          case 'INVALID_API_KEY':
            errorMessage = i18n.t("settings.settings.ia.invalidApiKey");
            break;
          case 'RATE_LIMIT_EXCEEDED':
            errorMessage = i18n.t("settings.settings.ia.rateLimitExceeded");
            break;
          case 'ACCESS_DENIED':
            errorMessage = i18n.t("settings.settings.ia.accessDenied");
            break;
          case 'NETWORK_ERROR':
            errorMessage = i18n.t("settings.settings.ia.networkError");
            break;
          case 'INTERNAL_ERROR':
            errorMessage = i18n.t("settings.settings.ia.internalError");
            break;
        }
      } else if (error.request) {
        errorMessage = i18n.t("settings.settings.ia.networkConnectionError");
        errorCode = 'CONNECTION_ERROR';
      } else {
        errorMessage = error.message || errorMessage;
      }

      setApiKeyValidation({
        isValid: false,
        error: errorMessage,
        lastCheck: new Date(),
        errorCode
      });

      toastUtils.error("Erro na Configuração", errorMessage);
    } finally {
      setValidatingApiKey(false);
    }
  }, []);

  // Componente para renderizar o status da validação
  const renderApiKeyValidationStatus = () => {
    if (validatingApiKey) {
      return (
        <Box display="flex" alignItems="center"  style={{ marginTop: '8px' }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="primary">
            {i18n.t("settings.settings.ia.validatingApiKey")}...
          </Typography>
        </Box>
      );
    }

    if (apiKeyValidation.isValid === true) {
      return (
        <Box display="flex" alignItems="center"  style={{ marginTop: '8px' }}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="caption" color="success.main">
            {i18n.t("settings.settings.ia.validKeySuccess")}
            {apiKeyValidation.modelsAvailable && (
              <span> ({apiKeyValidation.modelsAvailable} {i18n.t("settings.settings.ia.modelsAvailable")})</span>
            )}
          </Typography>
          {apiKeyValidation.lastCheck && (
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: '8px' }}>
              ({new Date(apiKeyValidation.lastCheck).toLocaleTimeString()})
            </Typography>
          )}
        </Box>
      );
    }

    if (apiKeyValidation.isValid === false) {
      return (
        <Box display="flex" alignItems="center"  style={{ marginTop: '8px' }}>
          <ErrorIcon color="error" fontSize="small" />
          <Typography variant="caption" color="error.main">
            {apiKeyValidation.error}
          </Typography>
          {apiKeyValidation.lastCheck && (
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: '8px' }}>
              ({new Date(apiKeyValidation.lastCheck).toLocaleTimeString()})
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <form id="settings-form" noValidate>
      <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="info" style={{ marginBottom: '16px' }}>
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("settings.settings.ia.globalAiConfigTitle")}
          </Typography>
          <Typography variant="body2">
            {i18n.t("settings.settings.ia.globalAiConfigDesc")}
          </Typography>
        </Alert>
      </Grid>

      {/* Configuração Principal da OpenAI */}
      <Grid item xs={12} spacing={1}>
        <SettingTextField
          id="openAiKey"
          title={i18n.t("settings.settings.ia.openAiApiKey")}
          description={i18n.t("settings.settings.ia.openAiApiKeyDesc")}
          value={settingsLoaded.openAiKey}
          onChange={handleSettingChange} // Usar a função modificada
          type="password"
          icon={<SecurityIcon color="primary" />}
        />

        {/* Status da validação */}
        {renderApiKeyValidationStatus()}

        {/* Botão de validação */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <Button
            size="small"
            onClick={() => validateOpenAIKey(settingsLoaded.openAiKey)}
            disabled={!settingsLoaded.openAiKey || validatingApiKey || settingsLoaded.openAiKey.length < 40}
            startIcon={validatingApiKey ? <CircularProgress size={16} /> : <RefreshIcon />}
            variant="outlined"
            color="primary"
            >
            {validatingApiKey
                ? i18n.t("settings.settings.ia.validating")
                : i18n.t("settings.settings.ia.validateKey")
            }
          </Button>

          {/* Link para documentação */}
          <Typography variant="caption" color="textSecondary">
            <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                color: 'inherit',
                textDecoration: 'underline',
                '&:hover': { textDecoration: 'none' }
                }}
            >
                {i18n.t("settings.settings.ia.getApiKey")}
            </a>
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <SettingSelect
          id="openaiModel"
          title={i18n.t("settings.settings.ia.defaultOpenAiModel")}
          description={i18n.t("settings.settings.ia.defaultOpenAiModelDesc")}
          value={settingsLoaded.openaiModel || "gpt-4o"}
          options={openAiModels}
          onChange={handleSettingChange}
          icon={<PsychologyIcon color="primary" />}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <SettingSelect
          id="aiProvider"
          title={i18n.t("settings.settings.ia.aiProvider")}
          description={i18n.t("settings.settings.ia.aiProviderDesc")}
          value={settingsLoaded.aiProvider || "openai"}
          options={[
            { value: "openai", label: "OpenAI" }
          ]}
          onChange={handleSettingChange}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      <Grid item xs={12}>
        <SettingSwitch
          id="aiAutoShowSuggestions"
          title={i18n.t("settings.settings.ia.autoShowSuggestions")}
          description={i18n.t("settings.settings.ia.autoShowSuggestionsDesc")}
          value={settingsLoaded.aiAutoShowSuggestions || "disabled"}
          onChange={handleSettingChange}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      {/* Transcrição de Áudio */}
      <Grid item xs={12}>
        <SettingSwitch
          id="enableAudioTranscriptions"
          title={i18n.t("settings.settings.ia.enableAudioTranscription")}
          description={i18n.t("settings.settings.ia.enableAudioTranscriptionDesc")}
          value={settingsLoaded.enableAudioTranscriptions || "disabled"}
          onChange={handleSettingChange}
          icon={<MicIcon color="primary" />}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      {settingsLoaded.enableAudioTranscriptions === "enabled" && (
        <>
          <Grid item xs={12} md={6}>
            <SettingSelect
              id="transcriptionModel"
              title={i18n.t("settings.settings.ia.transcriptionModel")}
              description={i18n.t("settings.settings.ia.transcriptionModelDesc")}
              value={settingsLoaded.transcriptionModel || "whisper-1"}
              options={[
                { value: "whisper-1", label: "Whisper v1" },
                { value: "gpt-4o-mini-transcribe", label: "GPT-4o Mini Transcribe" }
              ]}
              onChange={handleSettingChange}
              disabled={!settingsLoaded.openAiKey}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingSelect
              id="transcriptionLanguage"
              title={i18n.t("settings.settings.ia.defaultTranscriptionLanguage")}
              description={i18n.t("settings.settings.ia.defaultTranscriptionLanguageDesc")}
              value={settingsLoaded.transcriptionLanguage || "pt"}
              options={[
                { value: "pt", label: i18n.t("settings.settings.ia.portuguese") },
                { value: "en", label: i18n.t("settings.settings.ia.english") },
                { value: "es", label: i18n.t("settings.settings.ia.spanish") },
                { value: "fr", label: i18n.t("settings.settings.ia.french") },
                { value: "de", label: i18n.t("settings.settings.ia.german") },
                { value: "it", label: i18n.t("settings.settings.ia.italian") },
                { value: "ja", label: i18n.t("settings.settings.ia.japanese") },
                { value: "ko", label: i18n.t("settings.settings.ia.korean") },
                { value: "zh", label: i18n.t("settings.settings.ia.chinese") },
                { value: "auto", label: i18n.t("settings.settings.ia.autoDetect") }
              ]}
              onChange={handleSettingChange}
              disabled={!settingsLoaded.openAiKey}
            />
          </Grid>
        </>
      )}

      {/* Síntese de Voz */}
      <Grid item xs={12}>
        <SettingSwitch
          id="voiceEnableResponses"
          title={i18n.t("settings.settings.ia.enableVoiceResponses")}
          description={i18n.t("settings.settings.ia.enableVoiceResponsesDesc")}
          value={settingsLoaded.voiceEnableResponses || "disabled"}
          onChange={handleSettingChange}
          icon={<VolumeUpIcon color="primary" />}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      {settingsLoaded.voiceEnableResponses === "enabled" && (
        <>
            <Grid item xs={12} md={6}>
            <SettingSelect
                id="voiceId"
                title={i18n.t("settings.settings.ia.defaultVoice")}
                description={i18n.t("settings.settings.ia.defaultVoiceDesc")}
                value={settingsLoaded.voiceId || "nova"}
                options={voiceOptions}
                onChange={handleSettingChange}
                disabled={!settingsLoaded.openAiKey}
            />
            </Grid>

            <Grid item xs={12} md={6}>
            <SettingSlider
                id="voiceSpeed"
                title={i18n.t("settings.settings.ia.speechSpeed")}
                description={i18n.t("settings.settings.ia.speechSpeedDesc")}
                value={Number(settingsLoaded.voiceSpeed) || 1.0}
                onChange={(id, value) => handleSettingChange(id, value.toString())}
                min={0.5}
                max={2.0}
                step={0.1}
                icon={<SpeedIcon color="primary" />}
                marks={[
                { value: 0.5, label: '0.5x' },
                { value: 1.0, label: '1.0x' },
                { value: 1.5, label: '1.5x' },
                { value: 2.0, label: '2.0x' }
                ]}
                formatLabel={(val) => `${val.toFixed(1)}x`}
                disabled={!settingsLoaded.openAiKey}
            />
            </Grid>
        </>
      )}

      {/* Resumo de grupos */}
      <Grid item xs={12}>
        <SettingSwitch
          id="iaEnableGroupResume"
          title={i18n.t("settings.settings.ia.enableGroupResume")}
          description={i18n.t("settings.settings.ia.enableGroupResumeDesc")}
          value={settingsLoaded.iaEnableGroupResume || "disabled"}
          onChange={handleSettingChange}
          icon={<SummarizeIcon color="primary" />}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      {settingsLoaded.iaEnableGroupResume === "enabled" && (
        <>
          {/* Configurações de Resumo de Grupos */}
          <Grid item xs={12} md={6}>
            <SettingSelect
              id="aiSummaryLanguage"
              title={i18n.t("settings.settings.ia.summaryDefaultLanguage")}
              description={i18n.t("settings.settings.ia.summaryDefaultLanguageDesc")}
              value={settingsLoaded.aiSummaryLanguage || "pt-BR"}
              options={[
                { value: "pt-BR", label: i18n.t("settings.ia.portugueseBrazil") },
                { value: "en-US", label: i18n.t("settings.ia.englishUS") },
                { value: "es-ES", label: i18n.t("settings.ia.spanishES") }
              ]}
              onChange={handleSettingChange}
              icon={<LanguageIcon color="primary" />}
              disabled={!settingsLoaded.openAiKey}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingSelect
              id="aiSummaryLength"
              title={i18n.t("settings.settings.ia.summaryDefaultLength")}
              description={i18n.t("settings.settings.ia.summaryDefaultLengthDesc")}
              value={settingsLoaded.aiSummaryLength || "medium"}
              options={[
                { value: "short", label: i18n.t("settings.ia.short") },
                { value: "medium", label: i18n.t("settings.ia.medium") },
                { value: "long", label: i18n.t("settings.ia.long") }
              ]}
              onChange={handleSettingChange}
              icon={<AssessmentOutlined color="primary" />}
              disabled={!settingsLoaded.openAiKey}
            />
          </Grid>

          <Grid item xs={12}>
            <SettingSwitch
              id="aiIncludeParticipantStats"
              title={i18n.t("settings.settings.ia.includeParticipantStatsDefault")}
              description={i18n.t("settings.settings.ia.includeParticipantStatsDefaultDesc")}
              value={settingsLoaded.aiIncludeParticipantStats || "enabled"}
              onChange={handleSettingChange}
              icon={<GroupIcon color="primary" />}
              disabled={!settingsLoaded.openAiKey}
            />
          </Grid>
        </>
      )}

      {/* Configurações Avançadas de IA */}
      <Grid item xs={12} md={6}>
        <SettingSlider
          id="aiTemperature"
          title={i18n.t("settings.settings.ia.defaultCreativity")}
          description={i18n.t("settings.settings.ia.defaultCreativityDesc")}
          value={Number(settingsLoaded.aiTemperature) || 0.7}
          onChange={(id, value) => handleSettingChange(id, value.toString())}
          min={0.1}
          max={1.0}
          step={0.1}
          icon={<TuneIcon color="primary" />}
          marks={[
            { value: 0.1, label: i18n.t("settings.settings.ia.conservative") },
            { value: 0.7, label: i18n.t("settings.settings.ia.balanced") },
            { value: 1.0, label: i18n.t("settings.settings.ia.creative") }
          ]}
          formatLabel={(val) => val.toFixed(1)}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <SettingTextField
          id="aiMaxTokens"
          title={i18n.t("settings.settings.ia.defaultMaxTokens")}
          description={i18n.t("settings.settings.ia.defaultMaxTokensDesc")}
          value={settingsLoaded.aiMaxTokens || "1500"}
          onChange={handleSettingChange}
          validation={numberValidation}
          disabled={!settingsLoaded.openAiKey}
        />
      </Grid>

      {/* Apollo.io Configuration Section for SDR */}
      <Grid item xs={12}>
        <Alert severity="info" style={{ marginBottom: '16px' }}>
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("settings.apollo.sectionTitle")}
          </Typography>
          <Typography variant="body2">
            {i18n.t("settings.apollo.sectionDescription")}
          </Typography>
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <SettingTextField
          id="apollioApiKey"
          title={i18n.t("settings.apollo.apiKey")}
          description={i18n.t("settings.apollo.apiKeyDescription")}
          value={settingsLoaded.apollioApiKey}
          onChange={handleSettingChange}
          type="password"
          icon={<SecurityIcon color="primary" />}
        />
      </Grid>

      <Grid item xs={12}>
        <SettingSwitch
          id="apollioEnabled"
          title={i18n.t("settings.apollo.enableIntegration")}
          description={i18n.t("settings.apollo.enableDescription")}
          value={settingsLoaded.apollioEnabled || "disabled"}
          onChange={handleSettingChange}
          disabled={!settingsLoaded.apollioApiKey}
        />
      </Grid>

      {settingsLoaded.apollioEnabled === "enabled" && (
        <>
          <Grid item xs={12} md={6}>
            <SettingTextField
              id="apollioMonthlySearchLimit"
              title={i18n.t("settings.apollo.monthlySearchLimit")}
              description={i18n.t("settings.apollo.monthlySearchDescription")}
              value={settingsLoaded.apollioMonthlySearchLimit || "1000"}
              onChange={handleSettingChange}
              type="number"
              validation={numberValidation}
              disabled={!settingsLoaded.apollioApiKey}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingTextField
              id="apollioMonthlyEnrichmentLimit"
              title={i18n.t("settings.apollo.monthlyEnrichmentLimit")}
              description={i18n.t("settings.apollo.monthlyEnrichmentDescription")}
              value={settingsLoaded.apollioMonthlyEnrichmentLimit || "500"}
              onChange={handleSettingChange}
              type="number"
              validation={numberValidation}
              disabled={!settingsLoaded.apollioApiKey}
            />
          </Grid>

          <Grid item xs={12}>
            <SettingTextField
              id="apollioDefaultLocation"
              title={i18n.t("settings.apollo.defaultLocation")}
              description={i18n.t("settings.apollo.defaultLocationDescription")}
              value={settingsLoaded.apollioDefaultLocation || "Brazil"}
              onChange={handleSettingChange}
              disabled={!settingsLoaded.apollioApiKey}
            />
          </Grid>
        </>
      )}

      {/* Dicas e Informações Finais */}
      <Grid item xs={12}>
        <Alert severity="info">
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("settings.settings.ia.configurationTipsTitle")}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>{i18n.t("settings.settings.ia.centralizedConfiguration")}:</strong> {i18n.t("settings.settings.ia.centralizedConfigurationDesc")}<br />
            <strong>{i18n.t("settings.settings.ia.openAiKey")}:</strong> {i18n.t("settings.settings.ia.openAiKeyDesc")}<br />
            <strong>{i18n.t("settings.settings.ia.recommendedModels")}:</strong> {i18n.t("settings.settings.ia.recommendedModelsDesc")}<br />
            <strong>{i18n.t("settings.settings.ia.voice")}:</strong> {i18n.t("settings.settings.ia.voiceDesc")}<br />
            <strong>{i18n.t("settings.settings.ia.performance")}:</strong> {i18n.t("settings.settings.ia.performanceDesc")}
          </Typography>
        </Alert>
      </Grid>

      {/* Status das Configurações de IA */}
      <Grid item xs={12}>
        {settingsLoaded.openAiKey ? (
          <Alert severity="success">
            <Typography variant="subtitle2" gutterBottom>
                {i18n.t("settings.settings.ia.aiConfigurationActive")}
            </Typography>
            <Typography variant="body2">
              {i18n.t("settings.settings.ia.currentAiSettings", {
                model: settingsLoaded.openaiModel || 'gpt-4o',
                transcription: settingsLoaded.enableAudioTranscriptions === 'enabled' ? i18n.t("settings.settings.ia.enabled") : i18n.t("settings.settings.ia.disabled"),
                voice: settingsLoaded.voiceEnableResponses === 'enabled' ? i18n.t("settings.settings.ia.enabled") : i18n.t("settings.settings.ia.disabled"),
                suggestions: settingsLoaded.aiAutoShowSuggestions === 'enabled' ? i18n.t("settings.settings.ia.enabled") : i18n.t("settings.settings.ia.disabled")
              })}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning">
            <Typography variant="subtitle2" gutterBottom>
                {i18n.t("settings.settings.ia.aiNotConfiguredTitle")}
            </Typography>
            <Typography variant="body2">
                {i18n.t("settings.settings.ia.aiNotConfiguredDesc")}
            </Typography>
          </Alert>
        )}
      </Grid>
    </Grid>
    </form>
  )
}

export default IA;